import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const scenarios = [
  {
    id: 'calendar',
    label: 'Scheduling',
    subtitle: 'Calendar & time',
    icon: FiIcons.FiCalendar,
    alertTag: 'Calendar request',
    alertColor: 'text-[#8A6A2A]',
    alertBg: 'bg-[#FBF4E8]',
    alertBorder: 'border-[#DFC98A]',
    initialMessage: "Hey! Your dentist appointment on Wednesday clashes with your afternoon walk. Want me to reach out and try to move it to Thursday morning?",
    actions: [
      {
        label: 'Yes, email them',
        reply: "Done — I've emailed your dentist requesting a move to Thursday morning. I'll notify you and update your calendar once they confirm. Could take a day or two.",
        followUp: {
          message: "In the meantime, should I protect Wednesday afternoons going forward so this kind of clash doesn't happen again?",
          actions: [
            { label: 'Yes, block them', result: "Wednesday afternoons are now blocked as personal time each week. No one can schedule over them." },
            { label: 'Not every week', result: "Got it — I'll leave Wednesdays open but flag any future clashes before they become a problem." }
          ]
        }
      },
      {
        label: 'Leave it, I\'ll sort it',
        reply: "No problem — leaving the appointment as is. I'll add a reminder so you don't forget to rearrange your walk.",
        followUp: {
          message: "Want me to also add 30 minutes of travel buffer before the appointment on Wednesday?",
          actions: [
            { label: 'Yes please', result: "Done — 30 min travel time blocked before your dentist. You're all set for Wednesday." },
            { label: 'Not needed', result: "All good. I'll remind you the evening before so you're not caught off guard." }
          ]
        }
      }
    ]
  },
  {
    id: 'quiz',
    label: 'Is Claw for me?',
    subtitle: 'Find your use case',
    icon: FiIcons.FiZap,
    alertTag: 'Quick quiz',
    alertColor: 'text-[#6B2A8A]',
    alertBg: 'bg-[#F9EEF9]',
    alertBorder: 'border-[#E6CDE6]',
    initialMessage: "Wondering if a personal AI assistant makes sense for you? Let's figure it out. What's the most chaotic part of your week?",
    actions: [
      {
        label: 'Booking & moving meetings',
        reply: "That's a great fit. Claw handles the back-and-forth of finding times, sending invites, and chasing confirmations — so you never have to.",
        followUp: {
          message: "Do you also spend time planning travel or booking restaurants for work dinners?",
          actions: [
            { label: 'Yes, quite a lot', result: "Perfect. Claw can research options, shortlist the best ones, and handle bookings on your behalf — you just approve." },
            { label: 'Not really', result: "Makes sense. We'll focus on keeping your calendar clean and your days running smoothly." }
          ]
        }
      },
      {
        label: 'Life admin & errands',
        reply: "Claw is great for this. Gift research, service bookings, reminders, meal planning — the everyday stuff that quietly eats your time.",
        followUp: {
          message: "Does your inbox also stress you out, or is that more manageable?",
          actions: [
            { label: "It's a disaster", result: "Claw can summarise long threads, flag what actually needs your attention, and draft replies that sound like you." },
            { label: 'Inbox is fine', result: "Great — we'll focus on the life admin side and keep things off your plate without touching your messages." }
          ]
        }
      }
    ]
  },
  {
    id: 'reminders',
    label: 'Reminders',
    subtitle: 'Life admin',
    icon: FiIcons.FiBell,
    alertTag: 'Reminder nudge',
    alertColor: 'text-[#2A6B4A]',
    alertBg: 'bg-[#EBF5F0]',
    alertBorder: 'border-[#8ACAAA]',
    initialMessage: "Quick one — it's Jess's birthday on Saturday. You haven't sorted a gift yet. Want me to find some ideas based on what she's into?",
    actions: [
      {
        label: 'Yes, find ideas',
        reply: "Found 3 ideas: a pottery class (£45), a new cookbook she'd love, or a spa voucher. I've sent the links to your email — let me know which one and I'll help you sort it.",
        followUp: {
          message: "Once you've picked, want me to remind you to actually send or wrap it on Friday evening?",
          actions: [
            { label: 'Yes, remind me Friday', result: "Reminder set for Friday at 6PM — 'Jess's birthday gift!' 🎁 You've got this." },
            { label: 'I\'ll remember', result: "No problem — links are in your email whenever you're ready. Let me know if you want help with anything else." }
          ]
        }
      },
      {
        label: 'I already have something',
        reply: "Brilliant — sounds like you're ahead of it. Do you want a reminder to actually give it to her, or set something up for the day itself?",
        followUp: {
          message: "Want me to send you a nudge on Saturday morning so it's front of mind?",
          actions: [
            { label: 'Yes, Saturday morning', result: "Reminder set for Saturday 9AM — 'Jess's birthday today! 🎂' Enjoy celebrating." },
            { label: 'No, I\'m good', result: "Got it — sounds like you've got it covered. I'll stay out of the way on this one." }
          ]
        }
      }
    ]
  },
  {
    id: 'travel',
    label: 'Travel',
    subtitle: 'Trip planning',
    icon: FiIcons.FiCompass,
    alertTag: 'Trip research',
    alertColor: 'text-[#2A4A8A]',
    alertBg: 'bg-[#EBF0FB]',
    alertBorder: 'border-[#8AAAD8]',
    initialMessage: "You mentioned Lisbon in October. I've found return flights from £189 (Oct 9–14, direct). Want me to pull hotel options near the old town too?",
    actions: [
      {
        label: 'Yes, find hotels',
        reply: "Found 3 great options from £95/night — all near Alfama with strong reviews. I've sent a summary to your email. Let me know which one catches your eye and I can check availability.",
        followUp: {
          message: "Should I also draft a rough itinerary for the 5 days? I can include neighbourhoods, restaurants, and a Sintra day trip.",
          actions: [
            { label: 'Yes please', result: "Itinerary drafted and sent! Includes Sintra on day 3, 2 restaurant suggestions, and a walking route through Belém." },
            { label: 'Just the hotels for now', result: "No problem — hotel options are in your email. Just shout when you're ready for more and I'll pick up where we left off." }
          ]
        }
      },
      {
        label: 'Can we go later in Oct?',
        reply: "Checking… Oct 16–21 also has direct flights from £210 return — a bit more but still solid value, and the weather should be equally good.",
        followUp: {
          message: "Want me to compare both windows side by side — price, weather, and crowd levels — and send it to your email?",
          actions: [
            { label: 'Yes, compare them', result: "Comparison sent! Oct 9th is cheaper; Oct 16th is slightly quieter. Both are great — just depends on your schedule." },
            { label: 'Book the Oct 9th flights', result: "I'll send you the booking link for Oct 9th now. Once you confirm payment, I'll add the trip to your calendar and start on hotels." }
          ]
        }
      }
    ]
  },
];

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3 bg-white border border-[#E8E2DA] rounded-2xl rounded-tl-sm w-fit shadow-sm">
    {[0, 0.2, 0.4].map((delay, i) => (
      <motion.div key={i} animate={{ y: [0, -3, 0] }}
        transition={{ repeat: Infinity, duration: 0.6, delay }}
        className="w-1.5 h-1.5 bg-[#9E9690] rounded-full" />
    ))}
  </div>
);

const ScenarioTab = ({ scenario, isActive, onClick }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 text-left relative overflow-hidden ${
      isActive ? 'bg-[#F4F9FD] border-[#C8DDE9]' : 'bg-white border-[#E8E2DA] hover:bg-[#F8F6F3] hover:border-[#D4CEC8]'
    }`}>
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
      isActive ? 'bg-[#1A1A18] text-white' : 'bg-[#F0ECE8] text-[#1A1A18]'
    }`}>
      <SafeIcon icon={scenario.icon} className="text-[15px]" />
    </div>
    <div className="flex-grow min-w-0">
      <h4 className="text-[14px] font-semibold text-[#1A1A18] leading-tight">{scenario.label}</h4>
      <p className="text-[11px] text-[#9E9690] mt-0.5">{scenario.subtitle}</p>
    </div>
    {isActive && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-7 bg-[#2D6A8F] rounded-l-full" />}
  </button>
);

const HowItWorks = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const chatBodyRef = useRef(null);
  const activeScenario = scenarios[activeIdx];

  useEffect(() => {
    setMessages([{ role: 'assistant', content: activeScenario.initialMessage, type: 'initial' }]);
    setActiveStep({ actions: activeScenario.actions });
    setIsTyping(false);
  }, [activeIdx]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isTyping, activeStep]);

  const handleActionClick = (action) => {
    setMessages(prev => [...prev, { role: 'user', content: action.label }]);
    setActiveStep(null);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      if (action.followUp) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: action.reply, type: 'text' },
          { role: 'assistant', content: action.followUp.message, type: 'followup' }
        ]);
        setActiveStep({ actions: action.followUp.actions });
      } else if (action.result) {
        setMessages(prev => [...prev, { role: 'assistant', content: action.result, type: 'success' }]);
      }
    }, 1100);
  };

  return (
    <section id="how-it-works" className="py-28 bg-[#F8F6F3] relative">
      <div className="absolute inset-0 notebook-bg pointer-events-none" />
      <div className="absolute top-0 bottom-0 left-[88px] w-px bg-[rgba(220,80,80,0.14)] hidden lg:block" />

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">

          {/* Left Column */}
          <div className="lg:col-span-5 lg:pr-2">
            <p className="text-[#1A1A18] font-medium text-sm mb-3">Interactive demo</p>
            <h2 className="text-4xl md:text-[48px] font-extrabold text-[#1A1A18] mb-5 leading-[1.05] tracking-tight"
              style={{ letterSpacing: '-0.03em' }}>
              See how Claw<br />actually thinks.
            </h2>
            <p className="text-[16px] text-[#6B6560] mb-8 leading-relaxed max-w-md">
              Claw doesn't just respond — it anticipates what you need next. Pick a scenario and see it in action.
            </p>
            <div className="space-y-2.5">
              {scenarios.map((s, idx) => (
                <ScenarioTab key={s.id} scenario={s} isActive={activeIdx === idx} onClick={() => setActiveIdx(idx)} />
              ))}
            </div>
            <div className="mt-7 pl-1">
              <p className="font-handwritten text-[18px] text-[#C17B4A]"
                style={{ transform: 'rotate(-1deg)', display: 'inline-block' }}>
                ← try clicking through each one
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-7 lg:sticky lg:top-24">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#F8F6F3] border border-[#E8E2DA] rounded-2xl overflow-hidden shadow-sm flex flex-col"
              style={{ height: '520px' }}>

              {/* Chat Header */}
              <div className="px-5 py-3.5 border-b border-[#EDE9E4] bg-white flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-[13px] bg-[#1A1A18] flex items-center justify-center text-lg shadow-inner">🦞</div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#3D7A5E] border-2 border-white rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold text-[#1A1A18]">Claw</h3>
                    <p className="text-[10px] text-[#9E9690]">Your personal assistant · active</p>
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${activeScenario.alertBg} ${activeScenario.alertColor} ${activeScenario.alertBorder}`}>
                  <SafeIcon icon={activeScenario.icon} className="text-[10px]" />
                  {activeScenario.label}
                </div>
              </div>

              {/* Chat Body */}
              <div ref={chatBodyRef}
                className="p-5 flex flex-col gap-4 overflow-y-auto flex-1 scroll-smooth"
                style={{ overscrollBehavior: 'contain' }}>
                <AnimatePresence mode="popLayout" initial={false}>
                  {messages.map((msg, idx) => (
                    <motion.div key={`msg-${idx}`}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22 }}
                      className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`flex items-start gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'assistant' && (
                          <span className="text-lg flex-shrink-0 mt-0.5">🦞</span>
                        )}
                        <div className={`max-w-[86%] px-4 py-3 text-[14px] leading-relaxed rounded-2xl shadow-sm ${
                          msg.role === 'user'
                            ? 'bg-[#1A1A18] text-[#F8F6F3] rounded-tr-sm'
                            : msg.type === 'success'
                            ? 'bg-[#EBF5F0] border border-[#8ACAAA] text-[#1A1A18] rounded-tl-sm'
                            : 'bg-white border border-[#E8E2DA] text-[#1A1A18] rounded-tl-sm'
                        }`}>
                          {msg.type === 'success' && (
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-[#3D7A5E] text-xs" />
                              <span className="text-[#2A6B4A] text-[10px] font-mono-custom uppercase tracking-wider font-bold">Done</span>
                            </div>
                          )}
                          <p>{msg.content}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {isTyping && (
                    <motion.div key="typing" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }} className="flex items-start gap-2.5">
                      <span className="text-lg flex-shrink-0 mt-0.5">🦞</span>
                      <TypingIndicator />
                    </motion.div>
                  )}

                  {activeStep && !isTyping && (
                    <motion.div key={`actions-${messages.length}`}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className="flex flex-wrap gap-2 ml-9">
                      {activeStep.actions.map((action, i) => (
                        <button key={i} onClick={() => handleActionClick(action)}
                          className={`px-4 py-2 text-[12px] font-semibold rounded-xl transition-all duration-200 border shadow-sm ${
                            i === 0
                              ? 'bg-[#1A1A18] text-[#F8F6F3] border-[#1A1A18] hover:bg-[#2A2A26]'
                              : 'bg-white text-[#1A1A18] border-[#E8E2DA] hover:bg-[#F0ECE8] hover:border-[#D4CEC8]'
                          }`}>
                          {action.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Chat Input */}
              <div className="px-5 py-3.5 bg-white border-t border-[#EDE9E4] flex items-center gap-2.5 flex-shrink-0">
                <div className="flex-1 bg-[#F8F6F3] border border-[#E8E2DA] rounded-full px-4 py-2.5 text-[12px] text-[#9E9690]">
                  Message Claw...
                </div>
                <div className="w-9 h-9 rounded-full bg-[#F0ECE8] text-[#9E9690] flex items-center justify-center border border-[#E8E2DA] pointer-events-none text-base">
                  ↑
                </div>
              </div>
            </motion.div>

            <p className="text-center text-[11px] text-[#9E9690] mt-3">
              * Simulated conversation based on live Claw interactions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;